/**
 * THE network layer. Every byte v2 sends or receives goes through here.
 * Timeout + retry with jittered backoff + circuit breaker, in one place,
 * tested once — instead of guards sprinkled across the app (v1's disease).
 */

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    public readonly body: unknown,
  ) {
    super(`HTTP ${status} ${url}`);
  }
}
export class NetworkDown extends Error {
  constructor(public readonly url: string, public readonly why: string) {
    super(`unreachable: ${url} (${why})`);
  }
}

export interface HttpOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
  /** per-attempt timeout; default 15s */
  timeoutMs?: number;
  /** total attempts; default 3 (GET) / 1 (mutations — never blind-retry writes) */
  attempts?: number;
  signal?: AbortSignal | undefined;
}

import { noteReadFail, noteReadOk } from "./health";

/** Circuit breaker per origin: after 4 consecutive network failures, fail fast
 * for 20s instead of hanging every caller (v1 froze whole tabs this way). */
const circuits = new Map<string, { fails: number; openUntil: number }>();

function circuit(origin: string) {
  let c = circuits.get(origin);
  if (!c) {
    c = { fails: 0, openUntil: 0 };
    circuits.set(origin, c);
  }
  return c;
}

/** exposed for the sync pill: is this origin currently considered down? */
export function originDown(origin: string): boolean {
  return circuit(origin).openUntil > Date.now();
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function http<T = unknown>(url: string, opts: HttpOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const attempts = opts.attempts ?? (method === "GET" ? 3 : 1);
  const timeoutMs = opts.timeoutMs ?? 15000;
  const origin = new URL(url).origin;
  const c = circuit(origin);
  const isRead = method === "GET"; // reads feed the staleness banner (health.ts)

  if (c.openUntil > Date.now()) {
    if (isRead) noteReadFail();
    throw new NetworkDown(url, "circuit-open");
  }

  let lastErr: unknown = null;
  for (let i = 0; i < attempts; i++) {
    const ctl = new AbortController();
    const onOuter = () => ctl.abort();
    opts.signal?.addEventListener("abort", onOuter);
    const t = setTimeout(() => ctl.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        method,
        headers: {
          ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
          ...opts.headers,
        },
        body: opts.body !== undefined ? JSON.stringify(opts.body) : null,
        signal: ctl.signal,
      });
      const text = await res.text();
      const data: unknown = text ? safeJson(text) : null;
      if (!res.ok) {
        // 5xx and 429 are retryable; 4xx are the caller's problem — no retry.
        if ((res.status >= 500 || res.status === 429) && i < attempts - 1) {
          lastErr = new HttpError(res.status, url, data);
          await sleep(backoff(i));
          continue;
        }
        c.fails = 0; // the server ANSWERED — network is fine
        if (isRead) noteReadFail(); // …but this read produced no fresh data
        throw new HttpError(res.status, url, data);
      }
      c.fails = 0;
      if (isRead) noteReadOk();
      return data as T;
    } catch (e) {
      if (e instanceof HttpError) throw e;
      // abort/timeout/dns — genuine network failure
      lastErr = e;
      c.fails++;
      if (c.fails >= 4) c.openUntil = Date.now() + 20000;
      if (i < attempts - 1) {
        await sleep(backoff(i));
        continue;
      }
    } finally {
      clearTimeout(t);
      opts.signal?.removeEventListener("abort", onOuter);
    }
  }
  if (isRead) noteReadFail();
  throw new NetworkDown(url, String((lastErr as Error)?.name ?? lastErr));
}

function backoff(attempt: number): number {
  const base = Math.min(8000, 500 * 2 ** attempt);
  return base / 2 + Math.random() * base; // full jitter
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
